/**
 * PCM audio playback from base64-encoded chunks.
 *
 * Decodes base64 PCM16 audio data and plays it through the Web Audio API.
 */

/**
 * PCM audio player that buffers and plays base64-encoded PCM16 chunks.
 */
export class PcmPlayback {
  private audioContext: AudioContext;
  private sampleRate: number;
  private nextStartTime = 0;
  private isPlaying = false;

  constructor(sampleRate = 24000) {
    this.sampleRate = sampleRate;
    this.audioContext = new AudioContext({ sampleRate });
  }

  /**
   * Enqueue a base64-encoded PCM16 chunk for playback.
   */
  play(base64: string): void {
    const bytes = this.base64ToBytes(base64);
    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);

    // Convert int16 to float32
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 0x8000;
    }

    const buffer = this.audioContext.createBuffer(
      1,
      float32.length,
      this.sampleRate,
    );
    buffer.getChannelData(0).set(float32);

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);

    // Schedule playback to avoid gaps
    const now = this.audioContext.currentTime;
    const startTime = Math.max(now, this.nextStartTime);
    source.start(startTime);
    this.nextStartTime = startTime + buffer.duration;
    this.isPlaying = true;
  }

  /**
   * Interrupt playback and clear the queue.
   */
  interrupt(): void {
    this.nextStartTime = 0;
    this.isPlaying = false;
    // Close and recreate context to immediately stop all sources
    this.audioContext.close();
    this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
    // New context may be suspended — resume so future play() calls work
    this.audioContext.resume().catch(() => {});
  }

  /**
   * Clean up audio resources.
   */
  dispose(): void {
    this.audioContext.close();
  }

  /**
   * Resume the AudioContext (required after user gesture).
   */
  async resume(): Promise<void> {
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
